import type { HandoverCount } from "./lianxi-handover.js";
import { appendProOffer } from "./pro-offer-link.js";

/**
 * What the finish of an Exercise 练习 says once its material is in 复习, for
 * both kinds.
 *
 * A reader without Pro 专业 is offered Pro after it, since a finish hands over
 * more cards than a free day reviews, and that backlog is what Pro is for. The
 * offer is the table's line for 复习 from `src/user/pro-offer.ts`, with its
 * link. `lianxi-round.ts` and `lianxi-yufa-lines.ts` draw it.
 *
 * **The hand-over line names the cards and when the first is due** (#1209),
 * where the handler counted them. A new reader who finishes an Exercise and
 * is told nothing more has no reason to open 复习, and none of the earlier
 * cohort did. A row handed over before the count was kept keeps each kind's
 * own line.
 */

/** Where the finish has got to, as the keeper reports it. */
type FinishKeeper =
  | {
      readonly handedOver: boolean;
      readonly free?: boolean | undefined;
      readonly handoverCount?: HandoverCount | undefined;
    }
  | undefined;

/** What a hand-over line says went in, given the count of cards. */
export type CardsAdded = (cards: number) => string;

/** The line a drill and a grammar exercise give. */
export const cardsAdded: CardsAdded = (cards) =>
  `Added ${cards === 1 ? "1 card" : `${String(cards)} cards`} to Review.`;

/**
 * The words after the finish line.
 * @param handedOver - What the kind says once the material is in 复习, where
 * the cards were not counted.
 * @param keeper - What kept the place, where anything did.
 * @param now - The reader's present, which the due time is said against.
 * @param added - What the kind says went in, where the cards were counted.
 * @returns The hand-over line, or the empty string until the material is
 * handed over.
 */
export function afterFinish(
  handedOver: string,
  keeper: FinishKeeper,
  now: Date = new Date(),
  added: CardsAdded = cardsAdded,
): string {
  if (keeper?.handedOver !== true) {
    return "";
  }

  return keeper.handoverCount === undefined
    ? handedOver
    : handoverLine(keeper.handoverCount, now, added);
}

/** How long before a due time is still "now", for a card due a moment after the finish. */
const momentMs = 60_000;

/** The start of the reader's local day an instant falls on, in milliseconds. */
function localDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

/**
 * When the first card comes due, said against the reader's present and in
 * their own time zone.
 * @param dueAt - When it comes due.
 * @param now - The reader's present.
 * @returns The clause, as "ready now" or "due tomorrow".
 */
export function dueClause(dueAt: Date, now: Date): string {
  const wait = dueAt.getTime() - now.getTime();

  if (wait <= momentMs) {
    return "ready now";
  }

  if (wait < 60 * momentMs) {
    const minutes = Math.ceil(wait / momentMs);

    return `due in ${String(minutes)} minutes`;
  }

  const days = Math.round((localDay(dueAt) - localDay(now)) / 86_400_000);

  if (days === 0) {
    return "due later today";
  }

  return days === 1
    ? "due tomorrow"
    : `due on ${dueAt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}`;
}

/**
 * The hand-over line, naming the cards and when the first is due.
 * @param count - How many cards went in, and when the first is due.
 * @param now - The reader's present.
 * @param added - What the kind says went in.
 * @returns The line.
 */
export function handoverLine(
  count: HandoverCount,
  now: Date,
  added: CardsAdded = cardsAdded,
): string {
  const first = count.cards === 1 ? "It is" : "The first is";

  return `${added(count.cards)} ${first} ${dueClause(count.dueAt, now)}.`;
}

/**
 * Put the offer of Pro after a finish line, for a free reader whose material
 * is in 复习.
 * @param line - The element the finish line was just written into.
 * @param keeper - What kept the place, where anything did.
 */
export function offerAfterFinish(
  line: HTMLElement,
  keeper: FinishKeeper,
): void {
  if (keeper?.handedOver === true && keeper.free === true) {
    appendProOffer(line, "lianxi-finish", "fuxi");
  }
}

/** Lines joined with a space, the empty ones left out. */
export function joined(...lines: readonly string[]): string {
  return lines.filter((line) => line !== "").join(" ");
}
